import { Share2 } from "lucide-react";
import { Button, cn } from "@city-sage/aurum-avis-labs-components";
import { SocialShareDialog } from "./SocialShareDialog";

interface ShareButtonProps {
  // Share data
  url: string;
  title: string;
  description?: string;
  hashtags?: string[];
  
  // Appearance
  variant?: 'light' | 'dark';
  layout?: 'horizontal' | 'vertical';
  size?: 'sm' | 'md' | 'lg';
  
  // Behavior
  shareMethod?: 'dialog' | 'native';
  onClick?: () => void;
  showLabel?: boolean;
  label?: string;
}

export const ShareButton = ({
  url,
  title,
  description,
  hashtags = ['CitySage', 'Travel'],
  variant = 'light',
  layout = 'horizontal',
  size = 'md',
  shareMethod = 'dialog',
  onClick,
  showLabel = false,
  label = 'Share'
}: ShareButtonProps) => {
  const buttonSizes = {
    sm: "h-8 w-8",
    md: "h-10 w-10", 
    lg: "h-12 w-12"
  };

  const iconSizes = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6"
  };

  const textSizes = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-sm"
  };

  const buttonClasses = cn(
    "rounded-full transition-all duration-200",
    buttonSizes[size],
    variant === 'dark' ? [
      "backdrop-blur-md bg-black/30 text-white hover:bg-white/20"
    ] : [
      "bg-gray-100 text-gray-600 hover:bg-gray-200"
    ]
  );

  const labelClasses = cn(
    "font-medium",
    textSizes[size],
    variant === 'dark' ? "text-white/90" : "text-gray-600"
  );

  const handleNativeShare = async () => {
    if (onClick) {
      onClick();
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: description || `Check out: ${title}`,
          url
        });
      } catch (error) {
        // User cancelled sharing or error occurred
        console.log('Sharing cancelled or failed:', error);
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(url);
        // Could show a toast here
        console.log('Link copied to clipboard');
      } catch (error) {
        console.error('Failed to copy link:', error);
      }
    }
  };

  const renderButton = () => (
    <Button
      variant="ghost"
      size="icon"
      onClick={shareMethod === 'native' ? handleNativeShare : onClick}
      className={buttonClasses}
    >
      <Share2 className={iconSizes[size]} />
    </Button>
  );

  const renderContent = () => {
    if (layout === 'vertical') {
      return (
        <div className="text-center">
          {renderButton()}
          {showLabel && (
            <div className={cn(labelClasses, "mt-1")}>
              {label}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        {renderButton()}
        {showLabel && (
          <span className={labelClasses}>
            {label}
          </span>
        )}
      </div>
    );
  };

  // Use SocialShareDialog for dialog method
  if (shareMethod === 'dialog') {
    return (
      <SocialShareDialog
        url={url}
        title={title}
        description={description}
        hashtags={hashtags}
      >
        {renderContent()}
      </SocialShareDialog>
    );
  }

  // Return native share button
  return renderContent();
}; 